"use client"

import React, {useState, useRef, useEffect, useMemo, forwardRef, type ForwardedRef} from "react"
import Tippy from "@tippyjs/react/headless";
import "tippy.js/dist/tippy.css";

import {cn} from "@isudio/utils";

import {Input, Popover, PopoverTrigger, PopoverContent, useTippyRegistry} from "../"
import {Wheel} from "./Wheel"
import {useMergeRefs} from "@isudio/utils"
import {Label} from "../";

type DatePickerProps =
  React.InputHTMLAttributes<HTMLInputElement>
  & {
  value?: Date
  error?: any
  minAge?: number
  maxDate?: Date
  minDate?: Date
  onFocus?: () => void
  onDateChange?: (date: Date) => void
  onClose?: () => void
}


const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      className,
      minAge = 18,
      value,
      error,
      onFocus,
      onDateChange,
      onClose,
      minDate,
      maxDate,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      register,
      unregister
    } = useTippyRegistry();

    const internalRef = useRef<HTMLInputElement | null>(null);
    const initialVal = value ?? props.defaultValue ?? (internalRef.current?.value ?? "");
    const [hasValue, setHasValue] = useState(Boolean(String(initialVal).trim()));
    const [showError, setShowError] = useState(false);
    const [hovering, setHovering] = useState(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [open, setOpen] = useState(false)
    const [ignoreBlur, setIgnoreBlur] = useState(false)
    const [hasUserInteracted, setHasUserInteracted] = useState(false)

    // Calculate date bounds
    const today = new Date()
    const defaultMaxDate = maxDate || new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate())
    const defaultMinDate = minDate || new Date(1900, 0, 1)

    // Initialize with current value or default to max allowed date
    const initialDate = value || defaultMaxDate

    const [selectedYear, setSelectedYear] = useState(initialDate.getFullYear().toString())
    const [selectedMonth, setSelectedMonth] = useState(initialDate.getMonth().toString())
    const [selectedDay, setSelectedDay] = useState(initialDate.getDate().toString())

    // Generate years array
    const years = useMemo(() => {
      const yearsList: string[] = []
      for (let year = defaultMaxDate.getFullYear(); year >= defaultMinDate.getFullYear(); year--) {
        yearsList.push(year.toString())
      }
      return yearsList
    }, [defaultMaxDate, defaultMinDate])

    // Generate months array
    const months: any = useMemo(() => {
      const currentYear = Number.parseInt(selectedYear)
      const monthsList = []

      for (let i = 0; i < 12; i++) {
        const daysInMonth = new Date(currentYear, i + 1, 0).getDate()
        let hasValidDay = false

        for (let day = 1; day <= daysInMonth; day++) {
          const testDate = new Date(currentYear, i, day)
          if (testDate <= defaultMaxDate && testDate >= defaultMinDate) {
            hasValidDay = true
            break
          }
        }

        if (hasValidDay) {
          const date = new Date(2000, i, 1)
          monthsList.push({
            value: i.toString(),
            label: date.toLocaleDateString("en", {month: "short"}),
          })
        }
      }
      return monthsList
    }, [selectedYear, defaultMaxDate, defaultMinDate])

    const days = useMemo(() => {
      const year = Number.parseInt(selectedYear)
      const month = Number.parseInt(selectedMonth)
      const daysInMonth = new Date(year, month + 1, 0).getDate()

      const daysList: string[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        const testDate = new Date(year, month, day)
        // Only include days that result in 18+ years of age
        if (testDate <= defaultMaxDate && testDate >= defaultMinDate) {
          daysList.push(day.toString())
        }
      }
      return daysList
    }, [selectedYear, selectedMonth, defaultMaxDate, defaultMinDate])

    useEffect(() => {
      const domVal = internalRef.current?.value;
      const v = value ?? domVal ?? "";
      setHasValue(Boolean(String(v).trim()));
    }, [value]);

    const isResetRef = useRef(false)
    // Update local state when value prop changes
    useEffect(() => {
      if (value) {
        setSelectedYear(value.getFullYear().toString())
        setSelectedMonth(value.getMonth().toString())
        setSelectedDay(value.getDate().toString())
        setHasUserInteracted(true)
        isResetRef.current = false;
      } else if (value === null || value === undefined) {
        if (!hasUserInteracted || isResetRef.current) {
          setSelectedYear(defaultMaxDate.getFullYear().toString());
          setSelectedMonth(defaultMaxDate.getMonth().toString());
          setSelectedDay(defaultMaxDate.getDate().toString());
          setHasUserInteracted(false);
          isResetRef.current = false;
        }
      }
    }, [value, defaultMaxDate, hasUserInteracted])

    useEffect(() => {
      // This effect runs when the component is remounted due to key change
      // Mark this as a reset scenario
      isResetRef.current = true;
      setSelectedYear(defaultMaxDate.getFullYear().toString())
      setSelectedMonth(defaultMaxDate.getMonth().toString())
      setSelectedDay(defaultMaxDate.getDate().toString())
      setHasUserInteracted(false)
    }, [])

    // Validate and adjust day when month/year changes
    useEffect(() => {
      const maxDay = days.length
      const currentDay = Number.parseInt(selectedDay)
      if (currentDay > maxDay) {
        setSelectedDay(maxDay.toString())
      }
    }, [days, selectedDay])

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const startHideTimer = (delay = 2000) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setShowError(false);
        hideTimerRef.current = null;
      }, delay);
    };

    useEffect(() => {
      // Ensure selected month is valid
      const validMonths = months.map((m: any) => m.value)
      if (!validMonths.includes(selectedMonth) && validMonths.length > 0) {
        setSelectedMonth(validMonths[0])
      }

      // Ensure selected day is valid
      if (!days.includes(selectedDay) && days.length > 0) {
        setSelectedDay(days[days.length - 1] as string) // Choose the last valid day (closest to maxDate)
      }
    }, [months, days, selectedMonth, selectedDay])

    useEffect(() => {
      if (error && !hovering) {
        setShowError(true);
        startHideTimer();
      } else if (!error) {
        setShowError(false);
        clearHideTimer();
      }
      return clearHideTimer;
    }, [error]);

    const handleMouseEnter = () => {
      if (!error) return;
      clearHideTimer();
      setHovering(true);
      setShowError(true);
    };

    const handleMouseLeave = () => {
      setHovering(false);
      setShowError(false);
      clearHideTimer();
    };

    // Handle date changes
    const handleDateChange = (year: string, month: string, day: string) => {
      const newDate = new Date(Number.parseInt(year), Number.parseInt(month), Number.parseInt(day))

      // Validate against bounds
      if (newDate >= defaultMinDate && newDate <= defaultMaxDate) {
        setHasUserInteracted(true)
        setHasValue(Boolean(String(newDate).trim()));
        onDateChange?.(newDate)
      }
    }

    const handleYearChange = (year: string) => {
      setSelectedYear(year)
      handleDateChange(year, selectedMonth, selectedDay)
    }

    const handleMonthChange = (month: string) => {
      setSelectedMonth(month)
      handleDateChange(selectedYear, month, selectedDay)
    }

    const handleDayChange = (day: string) => {
      setSelectedDay(day)
      handleDateChange(selectedYear, selectedMonth, day)
    }

    const handlePopoverMouseDown = (e: React.MouseEvent) => {
      e.preventDefault()
      setIgnoreBlur(true)
    }

    const handlePopoverMouseUp = () => {
      setTimeout(() => {
        setIgnoreBlur(false)
      }, 100)
    }

    const handlePopoverFocus = (e: Event) => {
      e.preventDefault()
    }

    const handlePopoverClose = (e: Event) => {
      e.preventDefault()

      onClose?.();
      if (error) {
        setShowError(true);
        startHideTimer();
      }
    }

    const handleInputClick = (e: React.MouseEvent) => {
      e.preventDefault()
    }

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.();
      e.target.setSelectionRange(0, 0);
      setOpen(true)
    }

    const handleInputBlur = () => {
      if (ignoreBlur) return

      setTimeout(() => {
        setOpen(false)
      }, 150)
    };

    const currentDisplayValue = useMemo(() => {
      if (!hasUserInteracted) return ""

      const year = Number.parseInt(selectedYear)
      const month = Number.parseInt(selectedMonth)
      const day = Number.parseInt(selectedDay)
      const currentDate = new Date(year, month, day)
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    }, [selectedYear, selectedMonth, selectedDay, hasUserInteracted])

    const inputEl = (
      <Tippy
        onCreate={instance => register(instance)}
        onDestroy={instance => unregister(instance)}
        className="err-tooltip"
        placement="bottom"
        visible={!!error && showError}
        interactive={false}
        hideOnClick={true}
        delay={0}
        render={(attrs) => (
          <div
            {...attrs}
            tabIndex={-1}
            className="err-tooltip-box box bg-[#640a0a] text-white text-sm px-2 py-1 rounded shadow whitespace-pre-line"
          >
            {error ?? ""}
            <div
              data-popper-arrow
              className="err-tooltip-arrow bg-[#640a0a]! w-3! h-3! rotate-45!"
            />
          </div>
        )}
      >
        <Input
          ref={useMergeRefs(internalRef, ref, (props as any).ref)}
          className={cn(
            "flex w-full cursor-pointer",
            error ? "border-input-field__error" : "",
            className
          )}
          readOnly={true}
          placeholder={props.placeholder}
          value={currentDisplayValue}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        />
      </Tippy>
    );

    return (
      <div
        className="relative flex-1">
        <Popover
          open={open}
          onOpenChange={setOpen}>
          <PopoverTrigger
            asChild>
            <div>
              {inputEl}
              {hasValue && (
                <Label className="absolute -top-2 left-2.25 text-[10px] font-semibold text-generic-dull">{props.placeholder}</Label>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="date-picker w-auto p-1"
            align="start"
            onMouseDown={handlePopoverMouseDown}
            onMouseUp={handlePopoverMouseUp}
            onOpenAutoFocus={handlePopoverFocus}
            onCloseAutoFocus={handlePopoverClose}
          >
            <div
              className="flex items-center justify-center gap-1">
              {/* Month Wheel */}
              <Wheel
                items={months.map((m: any) => m.label)}
                width={40}
                selected={months.find((m: any) => m.value === selectedMonth)?.label || months[0]?.label}
                onSelect={(monthLabel: string) => {
                  const monthData = months.find((m: any) => m.label === monthLabel)
                  if (monthData) {
                    handleMonthChange(monthData.value)
                  }
                }}
              />

              {/* Day Wheel */}
              <Wheel
                items={days}
                width={30}
                selected={selectedDay}
                onSelect={(day: string) => {
                  handleDayChange(day)
                }}
              />

              {/* Year Wheel */}
              <Wheel
                items={years}
                width={48}
                selected={selectedYear}
                onSelect={(year: string) => {
                  handleYearChange(year)
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

DatePicker.displayName = "DatePicker"
export {DatePicker}
